import React, {
  useEffect,
  useMemo,
  useState,
  type FC,
  type ReactNode,
} from 'react';
import { Box, GlobalStyles, Typography } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { motion, AnimatePresence as MotionPresence } from 'framer-motion';

import { useMytaverse } from '@providers/MytaverseProvider';
import { buildCinemaPalette } from '../DashboardContent/MytaStreams/LoadingCinema/palette';
import {
  CINEMA_FONTS_LINK_ID,
  CINEMA_FONTS_URL,
} from '../DashboardContent/MytaStreams/LoadingCinema/constants';

import {
  BRANDING_TRANSITION_BLUR_CLASS,
  BRANDING_TRANSITION_DETAIL,
  BRANDING_TRANSITION_DISMISS_LABEL,
  BRANDING_TRANSITION_DONE_HINT,
  BRANDING_TRANSITION_DONE_STEP,
  BRANDING_TRANSITION_FADE_SEC,
  BRANDING_TRANSITION_HEADLINE,
  BRANDING_TRANSITION_HINT,
  BRANDING_TRANSITION_KICKER,
  BRANDING_TRANSITION_STEPS,
} from './constants';
import {
  brandingStreamBlurGlobalStyles,
  brandingTransitionGlobalStyles,
  getBrandingTransitionStyles,
} from './styles';
import { useBrandingTransition } from './useBrandingTransition';

const AnimatePresence = MotionPresence as FC<{ children: ReactNode }>;

interface IBrandingTransitionScreenProps {
  isReady: boolean;
  onDismiss: () => void;
}

/**
 * Renders the cover itself — the frosted scrim and the card that narrates the re-skin.
 *
 * @param props.isReady - The hold is over: the steps stop, and the screen offers to be dismissed.
 * @param props.onDismiss - Takes the cover down. Only reachable once `isReady`.
 */
const BrandingTransitionScreen = ({
  isReady,
  onDismiss,
}: IBrandingTransitionScreenProps) => {
  const { appTheme } = useMytaverse();
  const palette = useMemo(() => buildCinemaPalette(appTheme), [appTheme]);
  const classes = useMemo(
    () => getBrandingTransitionStyles(palette),
    [palette],
  );

  const [stepIdx, setStepIdx] = useState(0);

  useEffect(() => {
    if (document.getElementById(CINEMA_FONTS_LINK_ID)) {
      return;
    }

    const link = document.createElement('link');
    link.id = CINEMA_FONTS_LINK_ID;
    link.rel = 'stylesheet';
    link.href = CINEMA_FONTS_URL;
    document.head.appendChild(link);
  }, []);

  useEffect(() => {
    const timers = BRANDING_TRANSITION_STEPS.slice(1).map((step, index) =>
      setTimeout(() => setStepIdx(index + 1), step.atMs),
    );

    return () => timers.forEach(clearTimeout);
  }, []);

  // The cover swallows every click and keystroke on its way to the world, so the button must not be
  // the only way out of it: a button that failed to paint would leave the user locked out of the map
  // with no gesture to try.
  useEffect(() => {
    if (!isReady) {
      return undefined;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onDismiss();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isReady, onDismiss]);

  return (
    <Box sx={classes.root} className="myta-branding-transition">
      <GlobalStyles styles={brandingTransitionGlobalStyles} />
      <Box sx={classes.backdrop} />
      <Box sx={classes.card} data-testid="branding-transition-card">
        <Typography component="span" sx={classes.kicker}>
          {BRANDING_TRANSITION_KICKER}
        </Typography>
        <Typography component="h2" sx={classes.headline}>
          {BRANDING_TRANSITION_HEADLINE}
        </Typography>
        <Typography component="p" sx={classes.detail}>
          {BRANDING_TRANSITION_DETAIL}
        </Typography>
        <Box sx={classes.stepLine}>
          <Box sx={isReady ? classes.stepDotDone : classes.stepDot} />
          <Typography
            component="span"
            sx={classes.stepName}
            data-testid="branding-transition-step"
          >
            {isReady
              ? BRANDING_TRANSITION_DONE_STEP
              : BRANDING_TRANSITION_STEPS[stepIdx].label}
          </Typography>
        </Box>
        <Box sx={classes.barTrack}>
          <Box sx={classes.barFill} />
        </Box>
        <Box sx={classes.footer}>
          <Typography component="p" sx={classes.hint}>
            {isReady ? BRANDING_TRANSITION_DONE_HINT : BRANDING_TRANSITION_HINT}
          </Typography>
          {isReady ? (
            <Box
              component="button"
              type="button"
              sx={classes.dismiss}
              onClick={onDismiss}
              data-testid="branding-transition-dismiss-button"
            >
              {BRANDING_TRANSITION_DISMISS_LABEL}
              <ArrowForwardIcon />
            </Box>
          ) : null}
        </Box>
      </Box>
    </Box>
  );
};

/**
 * Covers the map while it is re-dressed for a newly activated configuration, until the user takes
 * the cover down.
 *
 * The fading wrapper is keyed by nothing and the screen inside it by the transition id: a second
 * switch inside the window has to restart the steps and the bar, but keying the wrapper would make
 * AnimatePresence cross-fade one full-screen scrim over another — two darkenings stacked, and under
 * jsdom the exiting one never leaves at all.
 */
const BrandingTransition = () => {
  const { transitionId, isReady, dismiss } = useBrandingTransition();
  const covering = transitionId !== null;

  // The stream is blurred by a class on <body> rather than from inside the screen, because the
  // element that carries the video is not ours to render — each provider's library owns it.
  useEffect(() => {
    if (!covering) {
      return undefined;
    }

    document.body.classList.add(BRANDING_TRANSITION_BLUR_CLASS);

    return () => document.body.classList.remove(BRANDING_TRANSITION_BLUR_CLASS);
  }, [covering]);

  return (
    <>
      <GlobalStyles styles={brandingStreamBlurGlobalStyles} />
      <AnimatePresence>
        {transitionId === null ? null : (
          <motion.div
            key="branding-transition"
            data-testid="branding-transition"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: BRANDING_TRANSITION_FADE_SEC }}
            style={{ position: 'fixed', inset: 0, zIndex: 1400 }}
          >
            <BrandingTransitionScreen
              key={transitionId}
              isReady={isReady}
              onDismiss={dismiss}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default BrandingTransition;
